/**
 * The web room's bar for the call this person put on hold (the app's
 * HeldCallBar.native). Robert, 2026-10-12: "add hold screen to web portal",
 * with the hold rules of the app: Swap puts this call on hold and goes back
 * (a call with an interpreter is never held, so there it is End & go back),
 * Merge brings the held call's people here (P2P and group calls only), End
 * ends the held call. The held call is kept across the full page load each
 * room is (vrs_held_call, call-management/middleware.ts).
 */
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { endHeldCall, mergeHeldCall, switchToHeldCall } from '../../interpreter-queue/actions';
import { peekPersistentItem } from '../../vrs-auth/storage';
import { canHoldStoredCall } from '../holdPolicy';
/**
 * The call this page is in (vrs_active_call), peeked without a write-back.
 *
 * @returns {Object|null}
 */
function storedCall() {
    try {
        return JSON.parse(peekPersistentItem('vrs_active_call') || 'null');
    }
    catch {
        return null;
    }
}
const HeldCallBar = () => {
    const dispatch = useDispatch();
    const held = useSelector((state) => state['features/call-management']?.heldCall);
    const inConference = useSelector((state) => Boolean(state['features/base/conference']?.conference));
    const current = held && inConference ? storedCall() : null;
    const canSwap = canHoldStoredCall(current);
    const heldCallId = held?.callId || '';
    const onSwap = useCallback(() => dispatch(switchToHeldCall(heldCallId, canSwap ? 'hold' : 'end')), [canSwap, dispatch, heldCallId]);
    const onMerge = useCallback(() => dispatch(mergeHeldCall(heldCallId)), [dispatch, heldCallId]);
    const onEnd = useCallback(() => dispatch(endHeldCall(heldCallId)), [dispatch, heldCallId]);
    if (!held || !current?.callId || current.callId === held.callId) {
        return null;
    }
    const name = held.peerName || 'Someone';
    const canMerge = canSwap && canHoldStoredCall(held.record);
    return (React.createElement("div", { "aria-live": 'polite', className: 'held-call-bar', role: 'status' },
        React.createElement("span", { className: 'held-call-bar-label' },
            "On hold \u00B7 ",
            name),
        React.createElement("button", { "aria-label": canSwap
                ? `Put this call on hold and go back to ${name}` : `End this call and go back to ${name}`, className: 'held-call-bar-btn', onClick: onSwap, type: 'button' }, canSwap ? 'Swap' : 'End & go back'),
        canMerge ? (React.createElement("button", { "aria-label": `Bring ${name} into this call`, className: 'held-call-bar-btn', onClick: onMerge, type: 'button' }, "Merge")) : null,
        React.createElement("button", { "aria-label": `End the call with ${name}`, className: 'held-call-bar-btn end', onClick: onEnd, type: 'button' }, "End")));
};
export default HeldCallBar;
